import React, { useState, useMemo, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  useViewerVersions,
  useRevertVersion,
  useLabelVersion,
  type ViewerVersionEntry,
} from './use-viewer-version-queries'
import { useApiBase } from './api-base-provider'
import { useApiClient } from './api-client'

// ─── Icons ────────────────────────────────────────────
const ic = {
  x: 'M18 6L6 18M6 6l12 12',
  clock: 'M12 2a10 10 0 100 20 10 10 0 000-20zM12 6v6l4 2',
  revert: 'M1 4v6h6M3.51 15a9 9 0 105.64-8.36L3 10',
  eye: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z',
  eyeInner: 'M12 15a3 3 0 100-6 3 3 0 000 6z',
  tag: 'M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z',
  diff: 'M16 3h5v5M4 20L21 3M21 16v5h-5M15 15l6 6M4 4l5 5',
  check: 'M20 6L9 17l-5-5',
  edit: 'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z',
  externalLink: 'M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3',
  chevronDown: 'M6 9l6 6 6-6',
  chevronRight: 'M9 18l6-6-6-6',
  code: 'M16 18l6-6-6-6M8 6l-6 6 6 6',
}

function Icon({ d, size = 16, color }: { d: string; size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke={color || 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  )
}

function EyeIcon({ size = 16, color }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke={color || 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d={ic.eye} />
      <path d={ic.eyeInner} />
    </svg>
  )
}

function ClockIcon({ size = 16, color }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
         stroke={color || 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  )
}

// ─── Helpers ──────────────────────────────────────────

function timeAgo(dateString: string): string {
  const now = Date.now()
  const then = new Date(dateString).getTime()
  const seconds = Math.floor((now - then) / 1000)

  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return new Date(dateString).toLocaleDateString()
}

function formatDateTime(dateString: string): string {
  const d = new Date(dateString)
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  })
}

/** Category labels for the diff viewer */
const CATEGORY_LABELS: Record<string, string> = {
  general: 'General',
  dataConnections: 'Data connections',
  layout: 'Layout & main area',
  triggers: 'Triggers',
  styling: 'Styling',
  scripts: 'Scripts',
  advanced: 'Advanced settings',
}

// ─── Deep mainArea diff ──────────────────────────────
// Walks the widget tree and produces per-widget, per-field diffs
// instead of treating mainArea as one opaque blob.

type MainAreaDiffEntry = {
  label: string       // e.g. "areas[1] Table++ → query"
  delta: any          // jsondiffpatch-style delta tuple
}

/** Fields on layout containers (SplitLayout, TabLayout, etc.) that aren't widget configs */
const LAYOUT_META_FIELDS = new Set(['widget', 'areas', 'areaSizes', 'direction', 'tabLabels'])

/** Fields to skip in widget-level diffs (noise) */
const WIDGET_SKIP_FIELDS = new Set(['_id', '__v', 'id'])

/**
 * Flatten a mainArea widget tree into a path-indexed map.
 * e.g. { "root": {...}, "areas[0]": {...}, "areas[1]": {...}, "areas[1].areas[0]": {...} }
 */
function flattenWidgetTree(
  node: Record<string, any> | null | undefined,
  path = 'root',
): Map<string, Record<string, any>> {
  const result = new Map<string, Record<string, any>>()
  if (!node || typeof node !== 'object') return result

  result.set(path, node)

  if (Array.isArray(node.areas)) {
    for (let i = 0; i < node.areas.length; i++) {
      const childPath = path === 'root' ? `areas[${i}]` : `${path}.areas[${i}]`
      const childMap = flattenWidgetTree(node.areas[i], childPath)
      for (const [k, v] of childMap) result.set(k, v)
    }
  }

  return result
}

/** Get a short display label for a widget node */
function widgetLabel(node: Record<string, any>): string {
  return node?.widget || 'Widget'
}

/**
 * Diff two mainArea trees at the widget level.
 * Returns an array of labeled diff entries for display in the UI.
 */
function diffMainArea(
  fromArea: Record<string, any> | null | undefined,
  toArea: Record<string, any> | null | undefined,
): MainAreaDiffEntry[] {
  const results: MainAreaDiffEntry[] = []

  // Handle cases where mainArea itself is added/removed
  if (!fromArea && !toArea) return results
  if (!fromArea && toArea) {
    results.push({ label: 'mainArea', delta: [toArea] })
    return results
  }
  if (fromArea && !toArea) {
    results.push({ label: 'mainArea', delta: [fromArea, 0, 0] })
    return results
  }

  const fromFlat = flattenWidgetTree(fromArea)
  const toFlat = flattenWidgetTree(toArea)

  const allPaths = new Set([...fromFlat.keys(), ...toFlat.keys()])

  for (const path of allPaths) {
    const fromNode = fromFlat.get(path)
    const toNode = toFlat.get(path)

    if (!fromNode && toNode) {
      // Widget added
      results.push({
        label: `${path} ${widgetLabel(toNode)}`,
        delta: [toNode],
      })
      continue
    }

    if (fromNode && !toNode) {
      // Widget removed
      results.push({
        label: `${path} ${widgetLabel(fromNode)}`,
        delta: [fromNode, 0, 0],
      })
      continue
    }

    if (!fromNode || !toNode) continue

    // Both exist — diff individual fields within this widget
    const wLabel = widgetLabel(fromNode) !== widgetLabel(toNode)
      ? `${widgetLabel(fromNode)} → ${widgetLabel(toNode)}`
      : widgetLabel(fromNode)

    const fieldKeys = new Set([...Object.keys(fromNode), ...Object.keys(toNode)])

    for (const key of fieldKeys) {
      if (WIDGET_SKIP_FIELDS.has(key)) continue
      // Skip 'areas' — child widgets are handled by their own paths
      if (key === 'areas') continue

      const a = JSON.stringify(fromNode[key])
      const b = JSON.stringify(toNode[key])
      if (a === b) continue

      const fieldLabel = path === 'root'
        ? `mainArea.${key}`
        : `${path} ${wLabel} → ${key}`

      if (a === undefined) {
        results.push({ label: fieldLabel, delta: [toNode[key]] })
      } else if (b === undefined) {
        results.push({ label: fieldLabel, delta: [fromNode[key], 0, 0] })
      } else {
        results.push({ label: fieldLabel, delta: [fromNode[key], toNode[key]] })
      }
    }
  }

  return results
}

// ─── Main Component ──────────────────────────────────

type VersionHistoryDialogProps = {
  viewerId: string
  viewerTitle: string
  viewerPath: string
  projectPath: string
  onClose: () => void
  canEdit: boolean
}

export function VersionHistoryDialog({
                                       viewerId,
                                       viewerTitle,
                                       viewerPath,
                                       projectPath,
                                       onClose,
                                       canEdit,
                                     }: VersionHistoryDialogProps) {
  const { baseUrl } = useApiBase()
  const api = useApiClient()
  const versionsQuery = useViewerVersions(viewerId)
  const revertMutation = useRevertVersion()
  const labelMutation = useLabelVersion()

  // Fetch current live config to show as "Current" entry
  const currentConfigQuery = useQuery({
    queryKey: ['v2', baseUrl, 'app-config', projectPath, viewerPath],
    queryFn: async () => {
      const { data } = await api.get(`/api/projects/${projectPath}/viewers/${viewerPath}`)
      return data as Record<string, any>
    },
    // See useViewerVersions: overrides the app-wide refetchOnMount: false.
    refetchOnMount: 'always',
  })

  // State
  const [selectedVersion, setSelectedVersion] = useState<number | null>(null)
  const [compareFrom, setCompareFrom] = useState<number | null>(null)
  const [compareTo, setCompareTo] = useState<number | null>(null)
  const [mode, setMode] = useState<'view' | 'diff'>('view')
  const [revertConfirm, setRevertConfirm] = useState<number | null>(null)
  const [labelEdit, setLabelEdit] = useState<{ version: number; value: string } | null>(null)
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set())
  // The raw config is for the technically minded; everyone else gets the summary.
  const [showJson, setShowJson] = useState(false)

  const versions = versionsQuery.data ?? []

  // Build combined list: current live config as pseudo-entry at top, then history
  const allVersions = useMemo(() => {
    const list: ViewerVersionEntry[] = []

    // "Current" = the live config, shown as the latest entry
    if (currentConfigQuery.data) {
      const latestVersionNum = versions.length > 0 ? versions[0].version + 1 : 1
      list.push({
        id: '__current__',
        viewer: viewerId,
        version: latestVersionNum,
        snapshot: currentConfigQuery.data,
        delta: null,
        savedBy: null,
        savedAt: new Date().toISOString(),
        source: 'auto' as const,
        label: 'Current',
        changeCount: 0,
      })
    }

    list.push(...versions)
    return list
  }, [versions, currentConfigQuery.data, viewerId])

  // Diff computed client-side from snapshots already in memory — no extra API call
  const diffResult = useMemo(() => {
    if (mode !== 'diff' || compareFrom == null || compareTo == null) return null

    const fromEntry = allVersions.find(v => v.version === compareFrom)
    const toEntry = allVersions.find(v => v.version === compareTo)
    if (!fromEntry || !toEntry) return null

    const fromSnap = fromEntry.snapshot
    const toSnap = toEntry.snapshot
    const delta: Record<string, any> = {}

    const allKeys = new Set([...Object.keys(fromSnap), ...Object.keys(toSnap)])
    const skipKeys = new Set(['_id', '__v', 'id', 'createdAt', 'user', 'project'])

    for (const key of allKeys) {
      if (skipKeys.has(key)) continue
      if (key === 'mainArea') continue // handled separately below
      const a = JSON.stringify(fromSnap[key])
      const b = JSON.stringify(toSnap[key])
      if (a === b) continue

      if (a === undefined) {
        delta[key] = [toSnap[key]]  // added
      } else if (b === undefined) {
        delta[key] = [fromSnap[key], 0, 0]  // deleted
      } else {
        delta[key] = [fromSnap[key], toSnap[key]]  // modified
      }
    }

    // Deep mainArea diff — decompose the widget tree
    const mainAreaDelta = diffMainArea(fromSnap.mainArea, toSnap.mainArea)

    const fieldToCategory: Record<string, string> = {
      title: 'general', subtitle: 'general', path: 'general', public: 'general',
      infoMessage: 'general', popupInfoAtStart: 'general', anonymousLogin: 'general',
      bbsparqlendpoint: 'dataConnections', repoURL: 'dataConnections',
      localRepoURL: 'dataConnections', sparqlErrorMessage: 'dataConnections',
      layoutName: 'layout', pageWidth: 'layout',
      notShowTop: 'layout', notShowBottom: 'layout',
      triggers: 'triggers',
      css: 'styling', logoURL: 'styling', logoutbutton: 'styling',
      prejs: 'scripts',
    }
    const categories: Record<string, Record<string, any>> = {}
    for (const [field, value] of Object.entries(delta)) {
      const cat = fieldToCategory[field] || 'advanced'
      if (!categories[cat]) categories[cat] = {}
      categories[cat][field] = value
    }

    // Merge mainArea widget-level diffs into the layout category
    if (mainAreaDelta.length > 0) {
      if (!categories['layout']) categories['layout'] = {}
      for (const entry of mainAreaDelta) {
        categories['layout'][entry.label] = entry.delta
      }
    }

    const totalChanges = Object.values(categories).reduce(
      (sum, fields) => sum + Object.keys(fields).length, 0
    )

    return {
      hasDiff: totalChanges > 0,
      categories,
    }
  }, [mode, compareFrom, compareTo, allVersions])

  // Auto-select first (current) version if none selected
  const activeVersion = useMemo(() => {
    if (selectedVersion != null) {
      return allVersions.find(v => v.version === selectedVersion)
    }
    return allVersions[0] ?? null
  }, [allVersions, selectedVersion])

  // Handlers
  const handleSelectVersion = useCallback((v: ViewerVersionEntry) => {
    if (mode === 'diff') {
      // In diff mode: first click sets "from", second sets "to"
      if (compareFrom == null) {
        setCompareFrom(v.version)
      } else if (compareTo == null && v.version !== compareFrom) {
        // Ensure from < to
        const [lo, hi] = [compareFrom, v.version].sort((a, b) => a - b)
        setCompareFrom(lo)
        setCompareTo(hi)
      } else {
        // Reset
        setCompareFrom(v.version)
        setCompareTo(null)
      }
    } else {
      setSelectedVersion(v.version)
    }
  }, [mode, compareFrom, compareTo])

  const handleRevert = useCallback(() => {
    if (revertConfirm == null) return
    revertMutation.mutate(
      { viewerId, version: revertConfirm },
      {
        onSuccess: () => {
          setRevertConfirm(null)
          setSelectedVersion(null)
        },
        onError: () => alert('Failed to revert.'),
      }
    )
  }, [viewerId, revertConfirm, revertMutation])

  const handleLabelSave = useCallback(() => {
    if (!labelEdit) return
    labelMutation.mutate(
      { viewerId, version: labelEdit.version, label: labelEdit.value.trim() || null },
      { onSuccess: () => setLabelEdit(null) },
    )
  }, [viewerId, labelEdit, labelMutation])

  // The entry just before `v` in time, i.e. the next one down the newest-first list.
  const compareWithPrevious = useCallback((v: ViewerVersionEntry) => {
    const index = allVersions.findIndex(entry => entry.version === v.version)
    const previous = allVersions[index + 1]
    if (!previous) return
    setMode('diff')
    setCompareFrom(previous.version)
    setCompareTo(v.version)
  }, [allVersions])

  const toggleCategory = useCallback((cat: string) => {
    setCollapsedCategories(prev => {
      const next = new Set(prev)
      next.has(cat) ? next.delete(cat) : next.add(cat)
      return next
    })
  }, [])

  const openAppWithVersion = useCallback((snapshot: Record<string, any>) => {
    // Open the app's current URL in a new tab.
    // For now this just opens the current live app — a future enhancement
    // could pass a version query param to load the snapshot read-only.
    const appPath = snapshot.path || ''
    const baseOrigin = baseUrl || window.location.origin
    const url = `${baseOrigin}/projects/${projectPath}/${appPath}`
    window.open(url, '_blank')
  }, [baseUrl, projectPath])

  // ─── Render ─────────────────────────────────────────

  return (
    <>
      {/* Main dialog — nested inside overlay like V2Dialog */}
      <div className="v2-dialog-overlay" onClick={onClose}>
        <div
          className="v2-dialog"
          style={{
            maxWidth: 960,
            width: '95vw',
            // A fixed height, so switching between the summary and the JSON does not resize the dialog.
            height: '85vh',
            display: 'flex',
            flexDirection: 'column',
          }}
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="v2-dialog-header" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px' }}>
            <ClockIcon size={18} color="var(--v2-accent)" />
            <div style={{ flex: 1 }}>
              <div className="v2-dialog-title" style={{ fontSize: 15, fontWeight: 600 }}>
                Version history
              </div>
              <div style={{ fontSize: 12, color: 'var(--v2-text-muted)', marginTop: 2 }}>
                {viewerTitle}
              </div>
            </div>

            {/* Mode toggle */}
            <div style={{ display: 'flex', gap: 4 }}>
              <button
                className={`v2-btn v2-btn--sm ${mode === 'view' ? 'v2-btn--primary' : 'v2-btn--ghost'}`}
                onClick={() => { setMode('view'); setCompareFrom(null); setCompareTo(null) }}
              >
                <EyeIcon size={13} /> Browse
              </button>
              <button
                className={`v2-btn v2-btn--sm ${mode === 'diff' ? 'v2-btn--primary' : 'v2-btn--ghost'}`}
                onClick={() => { setMode('diff'); setSelectedVersion(null) }}
              >
                <Icon d={ic.diff} size={13} /> Compare
              </button>
            </div>

            <button className="v2-panel-close" onClick={onClose} type="button">
              <Icon d={ic.x} size={18} />
            </button>
          </div>

          {/* Body */}
          <div style={{
            display: 'flex',
            flex: 1,
            overflow: 'hidden',
            borderTop: '1px solid var(--v2-border)',
          }}>
            {/* Left: version list */}
            <div style={{
              width: 320,
              minWidth: 280,
              borderRight: '1px solid var(--v2-border)',
              overflowY: 'auto',
              flexShrink: 0,
            }}>
              {/* Timeline scrubber */}
              {allVersions.length > 1 && (
                <div style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid var(--v2-border)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}>
                <span style={{ fontSize: 11, color: 'var(--v2-text-muted)', minWidth: 24 }}>
                  v{allVersions[allVersions.length - 1]?.version}
                </span>
                  <div style={{
                    flex: 1, display: 'flex', alignItems: 'center', gap: 2,
                    padding: '0 4px',
                  }}>
                    {[...allVersions].reverse().map((v) => {
                      const isSelected = mode === 'view'
                        ? v.version === (selectedVersion ?? allVersions[0]?.version)
                        : v.version === compareFrom || v.version === compareTo
                      return (
                        <button
                          key={v.version}
                          onClick={() => handleSelectVersion(v)}
                          title={`v${v.version}${v.id === '__current__' ? ' (current)' : ''} — ${formatDateTime(v.savedAt)}`}
                          style={{
                            width: isSelected ? 12 : 8,
                            height: isSelected ? 12 : 8,
                            borderRadius: '50%',
                            border: 'none',
                            background: isSelected ? 'var(--v2-accent)' : 'var(--v2-border-strong)',
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                            flexShrink: 0,
                          }}
                        />
                      )
                    })}
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--v2-text-muted)', minWidth: 24, textAlign: 'right' }}>
                  v{allVersions[0]?.version}
                </span>
                </div>
              )}

              {/* Diff mode hint */}
              {mode === 'diff' && (
                <div style={{
                  padding: '8px 16px',
                  fontSize: 12,
                  color: 'var(--v2-text-muted)',
                  background: 'var(--v2-accent-soft)',
                  borderBottom: '1px solid var(--v2-border)',
                }}>
                  {compareFrom == null
                    ? 'Select the first version to compare'
                    : compareTo == null
                      ? 'Now select the second version'
                      : `Comparing v${compareFrom} → v${compareTo}`}
                </div>
              )}

              {/* Version entries */}
              {versionsQuery.isLoading ? (
                <div style={{ padding: 40, textAlign: 'center' }}>
                  <div className="v2-spinner" />
                </div>
              ) : allVersions.length === 0 ? (
                <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--v2-text-muted)', fontSize: 13 }}>
                  No version history yet. Versions are created automatically when you save.
                </div>
              ) : (
                <div>
                  {allVersions.map((v) => {
                    const isCurrent = v.id === '__current__'
                    const isActive = mode === 'view' && v.version === (selectedVersion ?? allVersions[0]?.version)
                    const isDiffSelected = mode === 'diff' && (v.version === compareFrom || v.version === compareTo)

                    return (
                      <div
                        key={v.version}
                        onClick={() => handleSelectVersion(v)}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          borderBottom: '1px solid var(--v2-border)',
                          background: isActive || isDiffSelected
                            ? 'var(--v2-accent-soft)'
                            : undefined,
                          transition: 'background 0.1s',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{
                          fontSize: 12,
                          fontWeight: 600,
                          fontFamily: 'var(--v2-font-mono)',
                          color: isActive ? 'var(--v2-accent-text)' : 'var(--v2-text-primary)',
                        }}>
                          v{v.version}
                        </span>

                          {isCurrent && (
                            <span style={{
                              fontSize: 10,
                              padding: '1px 6px',
                              borderRadius: 9999,
                              background: 'var(--v2-success-bg, rgba(22, 163, 74, 0.1))',
                              color: 'var(--v2-success, #16a34a)',
                              fontWeight: 600,
                            }}>
                            current
                          </span>
                          )}

                          {v.source === 'revert' && !isCurrent && (
                            <span style={{
                              fontSize: 10,
                              padding: '1px 6px',
                              borderRadius: 9999,
                              background: 'var(--v2-warning-soft)',
                              color: 'var(--v2-warning)',
                              fontWeight: 600,
                            }}>
                            revert
                          </span>
                          )}

                          {v.label && !isCurrent && (
                            <span style={{
                              fontSize: 11,
                              padding: '1px 6px',
                              borderRadius: 4,
                              background: 'var(--v2-accent-soft)',
                              color: 'var(--v2-accent-text)',
                              maxWidth: 120,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                            {v.label}
                          </span>
                          )}

                          {v.changeCount > 0 && !isCurrent && (
                            <span style={{
                              fontSize: 10,
                              color: 'var(--v2-text-muted)',
                              marginLeft: 'auto',
                            }}>
                            {v.changeCount} {v.changeCount === 1 ? 'change' : 'changes'}
                          </span>
                          )}
                        </div>

                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          marginTop: 4,
                          fontSize: 11,
                          color: 'var(--v2-text-muted)',
                        }}>
                          <span>{isCurrent ? 'now' : timeAgo(v.savedAt)}</span>
                          {!isCurrent && (
                            <>
                              <span style={{ opacity: 0.4 }}>·</span>
                              <span>{v.savedBy?.name || v.savedBy?.email || 'Unknown'}</span>
                            </>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Right: detail pane */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {mode === 'view' && activeVersion ? (
                <>
                  {/* Version detail header */}
                  <div style={{
                    padding: '12px 20px',
                    borderBottom: '1px solid var(--v2-border)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                  }}>
                  <span style={{ fontWeight: 600, fontSize: 14 }}>
                    {activeVersion.id === '__current__' ? 'Current version' : `Version ${activeVersion.version}`}
                  </span>
                    <span style={{ fontSize: 12, color: 'var(--v2-text-muted)' }}>
                    {activeVersion.id === '__current__' ? 'Live config' : formatDateTime(activeVersion.savedAt)}
                  </span>
                    <div style={{ flex: 1 }} />

                    {/* Label button — not for current */}
                    {activeVersion.id !== '__current__' && (
                      labelEdit?.version === activeVersion.version ? (
                        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                          <input
                            className="v2-input"
                            style={{ fontSize: 12, padding: '4px 8px', width: 140 }}
                            value={labelEdit.value}
                            onChange={e => setLabelEdit({ ...labelEdit, value: e.target.value })}
                            onKeyDown={e => e.key === 'Enter' && handleLabelSave()}
                            placeholder="e.g. before refactor"
                            autoFocus
                          />
                          <button className="v2-btn v2-btn--primary v2-btn--sm" onClick={handleLabelSave}
                                  disabled={labelMutation.isPending}>
                            <Icon d={ic.check} size={12} />
                          </button>
                          <button className="v2-btn v2-btn--ghost v2-btn--sm" onClick={() => setLabelEdit(null)}>
                            <Icon d={ic.x} size={12} />
                          </button>
                        </div>
                      ) : (
                        <button
                          className="v2-btn v2-btn--ghost v2-btn--sm"
                          onClick={() => setLabelEdit({
                            version: activeVersion.version,
                            value: activeVersion.label || '',
                          })}
                          title="Add label"
                        >
                          <Icon d={ic.tag} size={12} /> {activeVersion.label || 'Label'}
                        </button>
                      )
                    )}

                    {/* Open app — only for current, old versions have the same path */}
                    {activeVersion.id === '__current__' && (
                      <button
                        className="v2-btn v2-btn--ghost v2-btn--sm"
                        onClick={() => openAppWithVersion(activeVersion.snapshot)}
                        title="Open app"
                      >
                        <Icon d={ic.externalLink} size={12} /> Open
                      </button>
                    )}

                    <button
                      className={`v2-btn v2-btn--sm ${showJson ? 'v2-btn--primary' : 'v2-btn--ghost'}`}
                      onClick={() => setShowJson(shown => !shown)}
                      aria-pressed={showJson}
                      title={showJson ? 'Show the summary' : 'Show the configuration as JSON'}
                    >
                      <Icon d={ic.code} size={12} /> JSON
                    </button>

                    {/* Revert — not for current */}
                    {canEdit && activeVersion.id !== '__current__' && (
                      <button
                        className="v2-btn v2-btn--ghost v2-btn--sm"
                        style={{ color: 'var(--v2-warning)' }}
                        onClick={() => setRevertConfirm(activeVersion.version)}
                        title="Revert to this version"
                      >
                        <Icon d={ic.revert} size={12} /> Revert
                      </button>
                    )}
                  </div>

                  {/* Snapshot: summary by default, the raw JSON on request */}
                  <div style={{ flex: 1, overflow: 'auto', padding: 0 }}>
                  {showJson ? (
                    <pre className="v2-code-block" style={{
                      margin: 0,
                      borderRadius: 0,
                      fontSize: 12,
                      lineHeight: 1.5,
                      minHeight: '100%',
                    }}>
                      {JSON.stringify(activeVersion.snapshot, null, 2)}
                    </pre>
                  ) : (
                    <VersionSummary
                      version={activeVersion}
                      hasPrevious={allVersions.findIndex(v => v.version === activeVersion.version) < allVersions.length - 1}
                      onCompareWithPrevious={() => compareWithPrevious(activeVersion)}
                    />
                  )}
                  </div>
                </>
              ) : mode === 'diff' && compareFrom && compareTo ? (
                <>
                  {/* Diff header */}
                  <div style={{
                    padding: '12px 20px',
                    borderBottom: '1px solid var(--v2-border)',
                    fontSize: 13,
                    fontWeight: 500,
                  }}>
                    Comparing v{compareFrom} → v{compareTo}
                  </div>

                  {/* Diff content */}
                  <div style={{ flex: 1, overflow: 'auto' }}>
                    {!diffResult?.hasDiff ? (
                      <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--v2-text-muted)', fontSize: 13 }}>
                        No differences between these versions.
                      </div>
                    ) : (
                      <DiffCategorizedView
                        categories={diffResult.categories}
                        collapsed={collapsedCategories}
                        onToggle={toggleCategory}
                      />
                    )}
                  </div>
                </>
              ) : (
                <div style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--v2-text-muted)',
                  fontSize: 13,
                }}>
                  {mode === 'diff'
                    ? 'Select two versions to compare'
                    : 'Select a version to view'}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Revert confirmation dialog */}
      {revertConfirm != null && (
        <div className="v2-dialog-overlay" style={{ zIndex: 1100 }} onClick={() => setRevertConfirm(null)}>
          <div
            className="v2-dialog"
            style={{ maxWidth: 420 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="v2-dialog-header" style={{ padding: '16px 20px' }}>
              <div className="v2-dialog-title">Revert to version {revertConfirm}?</div>
            </div>
            <div className="v2-dialog-body" style={{ padding: '12px 20px' }}>
              <p style={{ fontSize: 13, color: 'var(--v2-text-secondary)', lineHeight: 1.6, margin: 0 }}>
                This will restore the app configuration to the state it was in at version {revertConfirm}.
                The current configuration will be saved as a new version before reverting, so you can undo this action.
              </p>
            </div>
            <div className="v2-dialog-footer" style={{ padding: '12px 20px', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="v2-btn v2-btn--ghost" onClick={() => setRevertConfirm(null)}>
                Cancel
              </button>
              <button
                className="v2-btn v2-btn--primary"
                onClick={handleRevert}
                disabled={revertMutation.isPending}
              >
                {revertMutation.isPending ? 'Reverting...' : 'Revert'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}


// ─── Version Summary ─────────────────────────────────

/** What a version is, in words: the Browse view for people who do not read config JSON. */
function VersionSummary({
                          version,
                          hasPrevious,
                          onCompareWithPrevious,
                        }: {
  version: ViewerVersionEntry
  hasPrevious: boolean
  onCompareWithPrevious: () => void
}) {
  const isCurrent = version.id === '__current__'
  const { snapshot } = version
  // Layout containers (the nodes with child areas) are structure, not widgets anyone placed.
  const widgetCount = [...flattenWidgetTree(snapshot.mainArea).values()]
    .filter(node => !Array.isArray(node.areas) || node.areas.length === 0)
    .length

  const rows: [string, React.ReactNode][] = isCurrent
    ? [['Status', 'The configuration the app runs now']]
    : [
        ['Saved', formatDateTime(version.savedAt)],
        ['Saved by', version.savedBy?.name || version.savedBy?.email || 'Unknown'],
        ...(version.source === 'revert' ? [['Created by', 'Reverting to an earlier version'] as [string, string]] : []),
        ...(version.label ? [['Label', version.label] as [string, string]] : []),
        ['Changes', version.changeCount > 0
          ? `${version.changeCount} ${version.changeCount === 1 ? 'change' : 'changes'} compared to the version before`
          : 'No changes recorded'],
      ]

  rows.push(
    ['App title', snapshot.title || <span style={{ color: 'var(--v2-text-muted)' }}>none</span>],
    ['Path', <span style={{ fontFamily: 'var(--v2-font-mono)' }}>{snapshot.path || '—'}</span>],
    ['Visibility', snapshot.public ? 'Public' : 'Private'],
    ['Widgets', widgetCount],
  )

  return (
    <div style={{ padding: '16px 20px' }}>
      <dl style={{
        display: 'grid',
        gridTemplateColumns: 'max-content 1fr',
        columnGap: 24,
        rowGap: 10,
        margin: 0,
        fontSize: 13,
      }}>
        {rows.map(([label, value]) => (
          <React.Fragment key={label}>
            <dt style={{ color: 'var(--v2-text-muted)', fontWeight: 500 }}>{label}</dt>
            <dd style={{ margin: 0, color: 'var(--v2-text-primary)', overflowWrap: 'anywhere' }}>{value}</dd>
          </React.Fragment>
        ))}
      </dl>

      {hasPrevious && (
        <button className="v2-btn v2-btn--ghost v2-btn--sm" style={{ marginTop: 20 }} onClick={onCompareWithPrevious}>
          <Icon d={ic.diff} size={12} /> Compare with previous version
        </button>
      )}
    </div>
  )
}


// ─── Diff Categorized View ───────────────────────────

function DiffCategorizedView({
                               categories,
                               collapsed,
                               onToggle,
                             }: {
  categories: Record<string, Record<string, any>>
  collapsed: Set<string>
  onToggle: (cat: string) => void
}) {
  const catEntries = Object.entries(categories)

  if (catEntries.length === 0) {
    return (
      <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--v2-text-muted)', fontSize: 13 }}>
        No differences found.
      </div>
    )
  }

  return (
    <div>
      {catEntries.map(([cat, fields]) => {
        const isCollapsed = collapsed.has(cat)
        const fieldCount = Object.keys(fields).length

        return (
          <div key={cat} style={{ borderBottom: '1px solid var(--v2-border)' }}>
            {/* Category header */}
            <button
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 20px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                color: 'var(--v2-text-primary)',
                textAlign: 'left',
              }}
              onClick={() => onToggle(cat)}
            >
              <Icon d={isCollapsed ? ic.chevronRight : ic.chevronDown} size={12} />
              {CATEGORY_LABELS[cat] || cat}
              <span style={{
                fontSize: 10,
                padding: '1px 6px',
                borderRadius: 9999,
                background: 'var(--v2-accent-soft)',
                color: 'var(--v2-accent-text)',
                fontWeight: 600,
              }}>
                {fieldCount}
              </span>
            </button>

            {/* Fields */}
            {!isCollapsed && (
              <div style={{ padding: '0 20px 12px' }}>
                {Object.entries(fields).map(([field, delta]) => (
                  <DiffFieldEntry key={field} field={field} delta={delta} />
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}


// ─── Diff Field Entry ────────────────────────────────

function DiffFieldEntry({ field, delta }: { field: string; delta: any }) {
  // jsondiffpatch delta format:
  // [newValue]             → added
  // [oldValue, newValue]   → modified
  // [oldValue, 0, 0]      → deleted
  // For nested objects/arrays, it's a recursive structure

  let changeType: 'added' | 'modified' | 'deleted' | 'complex' = 'complex'
  let oldVal: any = undefined
  let newVal: any = undefined

  if (Array.isArray(delta)) {
    if (delta.length === 1) {
      changeType = 'added'
      newVal = delta[0]
    } else if (delta.length === 2) {
      changeType = 'modified'
      oldVal = delta[0]
      newVal = delta[1]
    } else if (delta.length === 3 && delta[1] === 0 && delta[2] === 0) {
      changeType = 'deleted'
      oldVal = delta[0]
    }
  }

  const colors = {
    added: { bg: 'var(--v2-success-bg, rgba(22, 163, 74, 0.1))', text: 'var(--v2-success, #16a34a)' },
    modified: { bg: 'var(--v2-accent-soft)', text: 'var(--v2-accent-text)' },
    deleted: { bg: 'var(--v2-danger-bg, rgba(220, 38, 38, 0.1))', text: 'var(--v2-danger, #dc2626)' },
    complex: { bg: 'var(--v2-accent-soft)', text: 'var(--v2-accent-text)' },
  }

  const c = colors[changeType]

  return (
    <div style={{
      marginBottom: 8,
      borderRadius: 6,
      border: '1px solid var(--v2-border)',
      overflow: 'hidden',
    }}>
      {/* Field name + change type */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 12px',
        background: 'var(--v2-bg-secondary)',
        fontSize: 12,
      }}>
        <code style={{
          fontFamily: 'var(--v2-font-mono)',
          fontWeight: 600,
          color: 'var(--v2-text-primary)',
        }}>
          {field}
        </code>
        <span style={{
          fontSize: 10,
          padding: '0 5px',
          borderRadius: 4,
          background: c.bg,
          color: c.text,
          fontWeight: 600,
          textTransform: 'uppercase',
        }}>
          {changeType}
        </span>
      </div>

      {/* Values */}
      <div style={{ padding: '8px 12px' }}>
        {changeType === 'added' && (
          <DiffValue label="+" value={newVal} color="var(--v2-success, #16a34a)" />
        )}
        {changeType === 'deleted' && (
          <DiffValue label="-" value={oldVal} color="var(--v2-danger, #dc2626)" />
        )}
        {changeType === 'modified' && (
          <>
            <DiffValue label="-" value={oldVal} color="var(--v2-danger, #dc2626)" />
            <DiffValue label="+" value={newVal} color="var(--v2-success, #16a34a)" />
          </>
        )}
        {changeType === 'complex' && (
          <pre style={{
            margin: 0,
            fontSize: 11,
            fontFamily: 'var(--v2-font-mono)',
            color: 'var(--v2-text-secondary)',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
            maxHeight: 200,
            overflow: 'auto',
          }}>
            {JSON.stringify(delta, null, 2)}
          </pre>
        )}
      </div>
    </div>
  )
}

function DiffValue({ label, value, color }: { label: string; value: any; color: string }) {
  const display = typeof value === 'string' ? value
    : typeof value === 'boolean' ? String(value)
      : JSON.stringify(value, null, 2)

  const isLong = display.length > 120

  return (
    <div style={{ display: 'flex', gap: 8, marginBottom: 4, alignItems: 'flex-start' }}>
      <span style={{
        color,
        fontWeight: 700,
        fontFamily: 'var(--v2-font-mono)',
        fontSize: 13,
        lineHeight: '20px',
        width: 14,
        textAlign: 'center',
        flexShrink: 0,
      }}>
        {label}
      </span>
      <pre style={{
        margin: 0,
        fontSize: 11,
        fontFamily: 'var(--v2-font-mono)',
        color: 'var(--v2-text-primary)',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-all',
        lineHeight: '20px',
        maxHeight: isLong ? 150 : undefined,
        overflow: isLong ? 'auto' : undefined,
      }}>
        {display}
      </pre>
    </div>
  )
}


// ─── Export trigger button (for use in MoreMenu / AppConfigPanel) ──

export function VersionHistoryButton({
                                       onClick,
                                       size = 14,
                                     }: {
  onClick: () => void
  size?: number
}) {
  return (
    <button
      className="v2-btn v2-btn--ghost v2-btn--sm"
      onClick={onClick}
      title="Version history"
      type="button"
    >
      <ClockIcon size={size} /> History
    </button>
  )
}
